import React, { useEffect, useState, useCallback } from 'react';
import { fieldVisibilityAPI, rbacAPI, formatApiErrorDetail } from '../api';
import { toast } from 'sonner';

// Reusable catalog of sensitive fields per module (extensible for future modules).
const CATALOG = {
  lead: ['customer_name', 'primary_mobile', 'alternative_number', 'whatsapp_number', 'email', 'company_name',
         'project_address', 'city', 'client_sector', 'project_type', 'plot_size', 'construction_area', 'lead_source', 'campaign_name',
         'follow_up_date', 'budget', 'priority', 'status', 'remarks'],
  client: ['client_name', 'mobile', 'email', 'address', 'city'],
  project: ['serial_no', 'site_address', 'plot_size', 'construction_area', 'project_value', 'current_stage', 'due_date'],
  architect_workspace: ['budget', 'final_deal_amount', 'advance_received', 'quotation'],
  financial: ['budget', 'final_deal_amount', 'advance_received', 'total_received', 'outstanding', 'profit', 'payment'],
  b2b: ['company_name', 'contact_person', 'mobile', 'alternate_mobile', 'email', 'address', 'city', 'gstin', 'assigned_to_name', 'total_business', 'received', 'outstanding', 'project_history', 'service_wise_business'],
  // Lead Detail (360°) cards — unchecking a role removes that card completely from its UI (backend enforced)
  lead_card: ['client_requirement', 'lead_source', 'files_media', 'pinned_note', 'activity_timeline', 'sales_pipeline', 'commercial_summary', 'project_details', 'next_followup'],
};
// Primary Mobile is permanently mandatory; customer_name keeps today's default until changed.
const LOCKED_REQUIRED = ['primary_mobile'];
const DEFAULT_REQUIRED = ['primary_mobile', 'customer_name'];

const ENTITIES = [
  { key: 'lead', label: 'Leads' },
  { key: 'client', label: 'Client Details' },
  { key: 'project', label: 'Project Details' },
  { key: 'architect_workspace', label: 'Architect Workspace' },
  { key: 'financial', label: 'Financial / Payment' },
  { key: 'b2b', label: 'B2B Partner Details' },
  { key: 'lead_card', label: 'Lead Detail Cards' },
];

export const FieldVisibilityPage = () => {
  const [entity, setEntity] = useState('lead');
  const [roles, setRoles] = useState([]);
  const [config, setConfig] = useState({}); // key `${entity}.${field}` -> {visible_to_roles, hidden}
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, cfgRes] = await Promise.all([rbacAPI.getRoles(), fieldVisibilityAPI.list()]);
      setRoles((rolesRes.data || []).map(r => r.role).filter(r => r !== 'super_admin'));
      const map = {};
      (cfgRes.data || []).forEach(c => {
        map[`${c.entity}.${c.field}`] = {
          visible_to_roles: c.visible_to_roles || [], hidden: !!c.hidden,
          required_on_create: c.required_on_create ?? DEFAULT_REQUIRED.includes(c.field)
        };
      });
      setConfig(map);
    } catch (e) {
      toast.error('Failed to load field visibility');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // An empty visible_to_roles means "no role restriction" on the backend, so show it as all-allowed.
  const getEntry = (field) => {
    const c = config[`${entity}.${field}`];
    if (!c) return { visible_to_roles: roles, hidden: false, required_on_create: DEFAULT_REQUIRED.includes(field) };
    return { ...c, visible_to_roles: c.visible_to_roles.length ? c.visible_to_roles : roles };
  };

  const toggleRole = async (field, role) => {
    const entry = getEntry(field);
    const has = entry.visible_to_roles.includes(role);
    const next = has ? entry.visible_to_roles.filter(r => r !== role) : [...entry.visible_to_roles, role];
    await save(field, { ...entry, visible_to_roles: next });
  };

  const toggleHidden = async (field) => {
    const entry = getEntry(field);
    await save(field, { ...entry, hidden: !entry.hidden });
  };

  // Required-on-create reuses the same field_visibility record (backend enforces it on Add Lead)
  const toggleRequired = async (field) => {
    if (LOCKED_REQUIRED.includes(field)) return;
    const entry = getEntry(field);
    await save(field, { ...entry, required_on_create: !entry.required_on_create });
  };

  const save = async (field, patch) => {
    try {
      await fieldVisibilityAPI.set({ entity, field, visible_to_roles: patch.visible_to_roles, hidden: patch.hidden,
        ...(entity === 'lead' ? { required_on_create: !!patch.required_on_create } : {}) });
      setConfig(prev => ({ ...prev, [`${entity}.${field}`]: patch }));
      toast.success('Saved');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, 'Failed to save'));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6" data-testid="field-visibility-page">
      <div>
        <h1 className="text-3xl font-outfit font-semibold">Field Visibility</h1>
        <p className="text-muted-foreground mt-1">Control which roles can view sensitive fields. Backend enforces these rules — hiding is not cosmetic.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {ENTITIES.map(e => (
          <button key={e.key} onClick={() => setEntity(e.key)} data-testid={`fv-entity-${e.key}`}
            className={`px-4 py-2 rounded-lg text-sm border ${entity === e.key ? 'bg-primary text-white border-primary' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>{e.label}</button>
        ))}
      </div>
      <div className="clay-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Field</th>
              {roles.map(r => <th key={r} className="px-3 py-3 font-medium capitalize">{r.replace(/_/g, ' ')}</th>)}
              <th className="px-3 py-3 font-medium">Hidden (all)</th>
              {entity === 'lead' && <th className="px-3 py-3 font-medium">Required on create</th>}
            </tr>
          </thead>
          <tbody>
            {(CATALOG[entity] || []).map(field => {
              const entry = getEntry(field);
              return (
                <tr key={field} className="border-t border-slate-100 dark:border-slate-800" data-testid={`fv-row-${field}`}>
                  <td className="px-4 py-3 font-mono text-xs">{field}</td>
                  {roles.map(r => (
                    <td key={r} className="px-3 py-3 text-center">
                      <input type="checkbox" checked={!entry.hidden && entry.visible_to_roles.includes(r)} disabled={entry.hidden}
                        onChange={() => toggleRole(field, r)} data-testid={`fv-${field}-${r}`} />
                    </td>
                  ))}
                  <td className="px-3 py-3 text-center">
                    <input type="checkbox" checked={entry.hidden} onChange={() => toggleHidden(field)} data-testid={`fv-hidden-${field}`} />
                  </td>
                  {entity === 'lead' && (
                    <td className="px-3 py-3 text-center">
                      <input type="checkbox" checked={LOCKED_REQUIRED.includes(field) || !!entry.required_on_create}
                        disabled={LOCKED_REQUIRED.includes(field)} onChange={() => toggleRequired(field)}
                        data-testid={`fv-required-${field}`} />
                      {LOCKED_REQUIRED.includes(field) && <span className="ml-1 text-[10px] text-muted-foreground">locked</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Primary Mobile is always required and can never be made optional. Super Admin always sees every field. Unchecking a role hides that field from it via the backend.</p>
    </div>
  );
};
export default FieldVisibilityPage;
